/**
 * @file   src/components/AiChat/AiWantAdPreview.js
 * @brief  Preview card for AI-generated want ad data
 * @date   February, 2026
 * @author Jake Hokinson
 */
import React from "react";

const formatBudget = (amount) => {
  if (!amount) return "";
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  return `$${Math.round(amount / 1000)}k`;
};

const PROPERTY_TYPE_LABELS = {
  1: "Single Family",
  2: "Townhouse",
  3: "Condo/Co-op",
  4: "Multi-Family",
  5: "Mobile/Manufactured",
  6: "Farm/Ranch",
};

const AiWantAdPreview = ({ wantAdData, onEditReview, fairHousingStatus }) => {
  if (!wantAdData) return null;

  const {
    adTitle,
    location,
    city,
    state,
    minBdgt,
    maxBdgt,
    noBedRm,
    noBathRm,
    sqFtMin,
    sqFtMax,
    prprtyType,
    nice2Have,
    adDesc,
  } = wantAdData;

  const displayLocation = location || (city && state ? `${city}, ${state}` : "");
  const budgetRange = minBdgt && maxBdgt
    ? `${formatBudget(minBdgt)} – ${formatBudget(maxBdgt)}`
    : "";
  const sqFtDisplay = sqFtMin
    ? sqFtMax
      ? `${sqFtMin.toLocaleString()} – ${sqFtMax.toLocaleString()} sqft`
      : `${sqFtMin.toLocaleString()}+ sqft`
    : "";
  const propertyLabel = PROPERTY_TYPE_LABELS[prprtyType] || "";

  return (
    <div className="ai-wantad-preview">
      {fairHousingStatus && (
        <div className={`preview-fh-badge ${fairHousingStatus === 'clean' || fairHousingStatus === 'regenerated' ? 'fh-clean' : 'fh-warning'}`}>
          {fairHousingStatus === 'clean' && <span>✓ Fair Housing compliant</span>}
          {fairHousingStatus === 'regenerated' && <span>✓ Fair Housing compliant (auto-corrected)</span>}
          {fairHousingStatus === 'failed' && <span>⚠ Review for Fair Housing compliance</span>}
        </div>
      )}
      <div className="preview-title">{adTitle}</div>
      <div className="preview-location">
        {displayLocation}
        {propertyLabel ? ` · ${propertyLabel}` : ""}
      </div>

      {budgetRange && <div className="preview-budget">{budgetRange}</div>}

      <div className="preview-stats">
        {noBedRm && (
          <div className="preview-stat">
            <span className="stat-value">{noBedRm}</span>
            <span className="stat-label">bed{noBedRm > 1 ? "s" : ""}</span>
          </div>
        )}
        {noBathRm && (
          <div className="preview-stat">
            <span className="stat-value">{noBathRm}</span>
            <span className="stat-label">bath{noBathRm > 1 ? "s" : ""}</span>
          </div>
        )}
        {sqFtDisplay && (
          <div className="preview-stat">
            <span className="stat-value">{sqFtDisplay}</span>
          </div>
        )}
      </div>

      {nice2Have && nice2Have.length > 0 && (
        <>
          <div className="preview-n2h-label">Nice-to-Haves</div>
          <div className="preview-n2h-list">
            {nice2Have.map((item, idx) => (
              <span key={item.id || idx} className="preview-n2h-tag">
                {item.label}
              </span>
            ))}
          </div>
        </>
      )}

      {adDesc && <div className="preview-desc">{adDesc}</div>}

      <button className="ai-preview-edit-btn" onClick={onEditReview}>
        Edit & Review →
      </button>
    </div>
  );
};

export default AiWantAdPreview;
